import { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { opsApi } from "@/operations/lib/api";

const ROLE_LABEL = { admin: "Admin", management: "Management", finance: "Finance / Accounting",
  account_manager: "Account Manager", pm: "Project Manager", estimator: "Estimator",
  foreman: "Foreman" };

/** Settings › Permissions — who may move a project's status, plus the thresholds the project
 *  dashboard judges a budget and a schedule slide by. */
export default function OpsPermissionsPage() {
  const [d, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData(await opsApi.opsSettings()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load the permissions"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setBusy(true);
    try { setData(await opsApi.opsSettingsPatch(patch)); toast.success("Saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "That could not be saved"); }
    finally { setBusy(false); }
  };

  if (!d?.dashboard) {
    return <div className="flex items-center gap-2 py-16 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading permissions…
    </div>;
  }
  const cfg = d.dashboard;
  const roles = cfg.status_change_roles || [];

  const toggle = (role, on) => save({
    status_change_roles: on ? [...new Set([...roles, role])] : roles.filter((r) => r !== role),
  });

  return (
    <div className="space-y-5" data-testid="ops-permissions-page">
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-slate-400" />
          <h2 className="text-base font-semibold text-slate-900">Who may change a project status</h2>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          On a project the status itself is the control — clicking it opens the allowed moves.
          Anyone outside these roles still sees the status; it is simply locked. Admin always may.
        </p>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {(d.roles || []).map((role) => (
            <label key={role} className="flex items-center gap-2 text-sm">
              <Checkbox checked={roles.includes(role)} disabled={role === "admin" || !d.can_edit}
                        onCheckedChange={(v) => toggle(role, !!v)}
                        data-testid={`ops-perm-role-${role}`} />
              <span className={role === "admin" ? "text-slate-400" : "text-slate-700"}>
                {ROLE_LABEL[role] || role}{role === "admin" ? " — always" : ""}
              </span>
            </label>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-base font-semibold text-slate-900">Project dashboard thresholds</h2>
        <p className="mt-1 text-xs text-slate-500">
          The Budget line reads on budget while Est at Completion is within BOTH tolerances of the
          Revised Budget; outside either it reads over or under.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {[["budget_tolerance_pct", "Budget Tolerance (%)"],
            ["budget_tolerance_amount", "Budget Tolerance ($)"],
            ["slide_warn_days", "Slide Warning (days)"]].map(([key, label]) => (
            <label key={key} className="block">
              <span className="text-xs font-medium text-slate-500">{label}</span>
              <Input type="number" defaultValue={cfg[key]} disabled={!d.can_edit || busy}
                     onBlur={(e) => Number(e.target.value) !== Number(cfg[key])
                       && save({ [key]: Number(e.target.value) })}
                     className="mt-1 h-9 text-sm" data-testid={`ops-perm-${key}`} />
            </label>
          ))}
        </div>
        {!d.can_edit && (
          <p className="mt-3 text-xs text-amber-700">Only an admin can change these.</p>
        )}
      </section>
    </div>
  );
}
